import {
  useCallback,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";

const isBrowser = () => typeof globalThis.localStorage !== "undefined";

const subscribe = (callback: () => void) => {
  if (!isBrowser()) return () => {};
  globalThis.addEventListener("storage", callback);
  return () => globalThis.removeEventListener("storage", callback);
};

const readRaw = (key: string): string | null => {
  if (!isBrowser()) return null;
  try {
    return globalThis.localStorage.getItem(key);
  } catch {
    return null;
  }
};

export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  const [initial] = useState(initialValue);

  const raw = useSyncExternalStore(
    subscribe,
    () => readRaw(key),
    () => null
  );

  const value = useMemo<T>(() => {
    if (raw === null) return initial;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return initial;
    }
  }, [raw, initial]);

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      if (!isBrowser()) return;
      const current = (() => {
        const r = readRaw(key);
        if (r === null) return initial;
        try {
          return JSON.parse(r) as T;
        } catch {
          return initial;
        }
      })();
      const resolved =
        typeof next === "function" ? (next as (p: T) => T)(current) : next;
      try {
        globalThis.localStorage.setItem(key, JSON.stringify(resolved));
        globalThis.dispatchEvent(new StorageEvent("storage", { key }));
      } catch {
        // storage disabled / quota exceeded — ignore
      }
    },
    [key, initial]
  );

  return [value, setValue];
}
